




















































// @ts-nocheck
import { Component, Vue, Watch } from 'vue-property-decorator'
import { DataStore } from 'aws-amplify'
import { query as tenant } from '@/store/tenant'
import { mutateSnackbar } from '@/store/snackbar'
import Editor from '@/components/Editor.vue'
import { RowConfig } from '@/lib/RowConfig.interface'
import { GenerateLocaleDict } from '@/lib/Locale'
import { Announcement } from '../models/index'
import { copyFromDataStore } from '@/amplify'

// datetime-local inputs use "YYYY-MM-DDTHH:mm" in local time, DataStore stores ISO strings in UTC.
const pad = (n: number) => String(n).padStart(2, '0')
const toLocalInput = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const fromLocalInput = (value?: string | null) => (value ? new Date(value).toISOString() : null)

@Component<AnnouncementView>({
  components: {
    Editor
  },
  // @ts-ignore
  metaInfo() {
    return {
      title: this.$t('announcement.title')
    }
  },
  apollo: {
    tenant
  }
})
export default class AnnouncementView extends Vue {
  announcement = null
  // Stays null until the first save creates the tenant's record.
  announcementId: string | null = null
  isPreviewVisible = false
  isLoading = false

  @Watch('tenant', { immediate: true })
  async load() {
    if (!this.tenant) return
    this.isLoading = true
    try {
      const existing = await this.queryLatest()
      this.announcementId = existing?.id ?? null
      this.announcement = { ...this.emptyAnnouncement(), ...(existing ? copyFromDataStore(existing) : {}) }
      // Records created before the action button existed carry null for the new fields.
      this.announcement.actionCaption = this.announcement.actionCaption || GenerateLocaleDict()
      this.announcement.actionUrl = this.announcement.actionUrl || ''
      this.announcement.canBeDismissed = this.announcement.canBeDismissed === true
    } catch (err) {
      mutateSnackbar({ text: err, color: `error` }, this.$apollo)
    }
    this.isLoading = false
  }

  // The app shows the most recently updated announcement, so the CMS edits that same one.
  async queryLatest() {
    const items = await DataStore.query(Announcement, a => a.tenant.eq(this.tenant.id))
    return items.sort((a, b) => String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? '')))[0]
  }

  emptyAnnouncement() {
    return {
      title: GenerateLocaleDict(),
      content: GenerateLocaleDict(),
      startDate: null,
      endDate: null,
      isActive: false,
      actionCaption: GenerateLocaleDict(),
      actionUrl: '',
      canBeDismissed: false,
      tenant: this.tenant?.id
    }
  }

  // The app only renders the button when the URL is set, so an empty field is stored as null.
  get normalizedAnnouncement() {
    const actionUrl = (this.announcement.actionUrl || '').trim()
    return { ...this.announcement, actionUrl: actionUrl || null }
  }

  get editorRows(): RowConfig[] {
    return [
      { title: this.$t('announcement.contentTitle'), type: 'title', key: 'title' },
      { title: this.$t('announcement.content'), type: 'text', key: 'content' },
      { title: this.$t('announcement.actionCaption'), type: 'title', key: 'actionCaption' }
    ]
  }

  get startDateLocal() {
    return toLocalInput(this.announcement?.startDate)
  }

  set startDateLocal(value) {
    this.announcement.startDate = fromLocalInput(value)
  }

  get endDateLocal() {
    return toLocalInput(this.announcement?.endDate)
  }

  set endDateLocal(value) {
    this.announcement.endDate = fromLocalInput(value)
  }

  async save() {
    this.isLoading = true
    try {
      if (this.announcementId) {
        await DataStore.save(
          Announcement.copyOf(
            await DataStore.query(Announcement, this.announcementId),
            updated => {
              for (const [key, value] of Object.entries(this.normalizedAnnouncement)) {
                updated[key] = value
              }
            }
          )
        )
      } else {
        const saved = await DataStore.save(
          new Announcement({ ...this.normalizedAnnouncement, tenant: this.tenant?.id })
        )
        this.announcementId = saved.id
      }
      mutateSnackbar(
        { text: String(this.$t('announcement.saved')), color: `success` },
        this.$apollo
      )
    } catch (err) {
      mutateSnackbar({ text: err, color: `error` }, this.$apollo)
    }
    this.isLoading = false
  }
}
